"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTonality } from "@/contexts/studio-context";
import {
  ChordPracticeGrid,
  ChordPracticeTempo,
} from "@/components/ui/organisms/chord-practice";
import {
  diatonicChordsForKey,
  type ChordEntry,
  type DiatonicChord,
} from "@/lib/music/chord-catalog";
import { useChordLabel } from "@/hooks/use-notation";
import { HarmonyBreadcrumb } from "./harmony-breadcrumb";
import {
  DIATONIC_FUNCTION,
  FUNCTION_LABEL,
  POPULAR_PROGRESSIONS,
} from "./harmony-data";
import { HarmonyOverview } from "./harmony-overview";
import { HarmonyScaleDetail } from "./harmony-scale-detail";
import { ProgressionDrill } from "./progression-drill";
import { useScalePianoHints } from "./use-scale-piano-hints";
import type { HarmonyFocus, HarmonyTonality } from "./types";

interface HarmonyOverlayProps {
  focus: HarmonyFocus;
  onFocusChange: (focus: HarmonyFocus) => void;
}

/**
 * Overlay central de la tab Harmonie : rendu au-dessus du studio piano
 * dans le même slot que `ChordsOverlay`.
 *
 * Pousse les hints du studio piano selon le focus courant, au plus
 * une source pousse à la fois, jamais deux en même temps :
 * - overview / scale : `useScalePianoHints` (gamme + tonique). Même
 *                      teinte sur les 7 notes, la tonique reste mise
 *                      en valeur comme ancre visuelle.
 * - chord    : aucun hint depuis ce composant, `ChordPracticeGrid`
 *              pousse ses propres hints via `useStaticValidation`.
 * - progression : `useProgressionPianoHints` câblé à l'intérieur de
 *                 `ProgressionDrill` (suit le step courant).
 *
 * Les call sites existants de `ChordPracticeGrid` (mode `chords`,
 * route `/studio/chords?practice=…`) restent inchangés : on consomme le même
 * organism en lui passant l'`entry` dérivée du focus diatonique.
 */
export function HarmonyOverlay({ focus, onFocusChange }: HarmonyOverlayProps) {
  const chordLabel = useChordLabel();
  const [tonality] = useTonality();

  const overviewActive = focus.kind === "overview";
  const scaleActive = focus.kind === "scale";

  useScalePianoHints(tonality, overviewActive || scaleActive);

  const diatonic = useMemo<DiatonicChord[]>(
    () =>
      tonality
        ? diatonicChordsForKey(tonality.tonic, tonality.mode)
        : [],
    [tonality],
  );

  // Per-chord progress for the harmony tab, persists across focus
  // switches within the session, mirrors the shape used by
  // `studio-page` / `ChordsOverlay` so the same `ChordPracticeGrid`
  // contract applies (done set + onMarkDone).
  const [practiceProgress, setPracticeProgress] = useState<
    Record<string, Set<string>>
  >({});

  const handleMarkPracticeDone = useCallback(
    (chordId: string, posId: string) => {
      setPracticeProgress((prev) => {
        const existing = prev[chordId] ?? new Set<string>();

        if (existing.has(posId)) {
          return prev;
        }

        const next = new Set(existing);
        next.add(posId);

        return { ...prev, [chordId]: next };
      });
    },
    [],
  );

  if (!tonality) {
    return (
      <div className="h-full w-full flex items-center justify-center px-8 text-center text-[14px] text-text-muted italic">
        Sélectionne une tonalité dans la barre d&apos;outils pour explorer
        l&apos;atlas harmonique.
      </div>
    );
  }

  if (focus.kind === "overview") {
    return (
      <HarmonyOverview tonality={tonality} onFocusChange={onFocusChange} />
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <HarmonyBreadcrumb
        tonality={tonality}
        leafLabel={leafLabel(focus, diatonic, chordLabel)}
        leafBadge={leafBadge(focus)}
        onBackToOverview={() => onFocusChange({ kind: "overview" })}
      />
      <div className="flex-1 min-h-0">
        <FocusBody
          focus={focus}
          tonality={tonality}
          diatonic={diatonic}
          practiceProgress={practiceProgress}
          onMarkPracticeDone={handleMarkPracticeDone}
        />
      </div>
    </div>
  );
}

function FocusBody({
  focus,
  tonality,
  diatonic,
  practiceProgress,
  onMarkPracticeDone,
}: {
  focus: HarmonyFocus;
  tonality: HarmonyTonality;
  diatonic: DiatonicChord[];
  practiceProgress: Record<string, Set<string>>;
  onMarkPracticeDone: (chordId: string, posId: string) => void;
}) {
  if (focus.kind === "scale") {
    return <HarmonyScaleDetail tonality={tonality} />;
  }

  if (focus.kind === "chord") {
    const dc = diatonic[focus.degree];

    if (!dc) {
      return <FocusFallback label="Détail de l'accord" />;
    }

    return (
      <HarmonyChordFocus
        entry={dc.entry}
        done={practiceProgress[dc.entry.id] ?? new Set<string>()}
        onMarkDone={(posId) => onMarkPracticeDone(dc.entry.id, posId)}
      />
    );
  }

  if (focus.kind === "progression") {
    return (
      <ProgressionDrill
        tonality={tonality}
        progressionId={focus.id}
      />
    );
  }

  return <FocusFallback label="Détail de la progression" />;
}

/**
 * Wrapper accord avec switch Statique / Tempo, à l'identique de
 * `ChordsOverlay`. Le `mode` est local (resette quand l'accord change),
 * la progression `done` est passée par le parent (HarmonyOverlay) pour
 * persister entre les changements d'accord, c'est ce qui permet la
 * suggestion de passage au mode tempo une fois toutes les positions
 * statiques validées.
 */
function HarmonyChordFocus({
  entry,
  done,
  onMarkDone,
}: {
  entry: ChordEntry;
  done: ReadonlySet<string>;
  onMarkDone: (posId: string) => void;
}) {
  const [mode, setMode] = useState<"static" | "tempo">("static");

  useEffect(() => {
    setMode("static");
  }, [entry.id]);

  if (mode === "static") {
    return (
      <ChordPracticeGrid
        entry={entry}
        done={done}
        onMarkDone={onMarkDone}
        mode={mode}
        onModeChange={setMode}
        onClose={undefined}
        hideVariants
      />
    );
  }

  return (
    <ChordPracticeTempo
      entry={entry}
      staticDone={done.size}
      mode={mode}
      onModeChange={setMode}
      onClose={undefined}
    />
  );
}

function FocusFallback({ label }: { label: string }) {
  return (
    <div className="h-full w-full flex items-center justify-center px-8 text-center text-[14px] text-text-muted italic">
      {label} (à venir)
    </div>
  );
}

function leafLabel(
  focus: HarmonyFocus,
  diatonic: DiatonicChord[],
  chordLabel: (symbol: string) => string,
): string {
  if (focus.kind === "scale") {
    return "Gamme";
  }

  if (focus.kind === "chord") {
    const dc = diatonic[focus.degree];

    if (!dc) {
      return "Accord";
    }

    return `Accord · ${dc.roman} ${chordLabel(dc.entry.symbol)}`;
  }

  if (focus.kind === "progression") {
    const prog = POPULAR_PROGRESSIONS.find((p) => p.id === focus.id);

    return `Progression · ${prog?.numerals ?? ", "}`;
  }

  return "";
}

function leafBadge(focus: HarmonyFocus): string | undefined {
  if (focus.kind !== "chord") {
    return undefined;
  }

  const fn = DIATONIC_FUNCTION[focus.degree];

  if (!fn) {
    return undefined;
  }

  return FUNCTION_LABEL[fn];
}
